import { L } from '@lib';
import { Utils } from '@utils';
import { Form, Input, InputNumber } from 'antd';
import React, { useMemo } from 'react';
import './ColumnActions.style.css';

interface IEditableCell {
  required?: boolean;
  editing: boolean;
  dataIndex: string;
  inputType: 'text' | 'number';
  children: React.ReactNode;
  style?: React.CSSProperties;
  form: any;
  width: number;
  isExtra: boolean;
}

export const EditableCell: React.FC<IEditableCell> = ({
  required = false,
  editing,
  dataIndex,
  inputType,
  children,
  style,
  form,
  width,
  isExtra,
}) => {
  const isNumberType = inputType === 'number';
  const readonlyFields = useMemo(() => new Set(['type', 'vat', 'total']), []);

  const handleBlur = () => {
    if (readonlyFields.has(dataIndex)) return;

    const value = form.getFieldValue(dataIndex);
    if (value === undefined || value === '') {
      form.setFieldsValue({ [dataIndex]: 0 });
    }

    const values = form.getFieldsValue(true); // Get the latest form values

    switch (dataIndex) {
      case 'discount':
        form.setFieldsValue({
          total: Utils.calculateRowTotalPrice({ ...values, discount: value }),
        });
        break;
      case 'timeSpent':
        form.setFieldsValue({
          total: Utils.calculateRowTotalPrice({ ...values, duration: value }),
        });
        break;
      case 'costPrice':
        const newPriceFromCost = Utils.roundToTwoDecimals(value * (1 + values.margin / 100));
        form.setFieldsValue({
          price: newPriceFromCost,
          total: Utils.calculateRowTotalPrice({
            ...values,
            price: newPriceFromCost,
            duration: values.timeSpent,
          }),
        });
        break;
      case 'margin':
        const newPriceFromMargin = Utils.roundToTwoDecimals(values.costPrice * (1 + value / 100));
        form.setFieldsValue({
          price: newPriceFromMargin,
          total: Utils.calculateRowTotalPrice({
            ...values,
            price: newPriceFromMargin,
            duration: values.timeSpent,
          }),
        });
        break;
      case 'price':
        const newMargin =
          values.costPrice !== 0
            ? Utils.roundToTwoDecimals(((value - values.costPrice) / values.costPrice) * 100)
            : 0;
        form.setFieldsValue({
          margin: newMargin,
          total: Utils.calculateRowTotalPrice({
            ...values,
            price: value,
            duration: values.timeSpent,
          }),
        });
        break;
      default:
        break;
    }
  };

  const inputNode = useMemo(() => {
    if (readonlyFields.has(dataIndex)) {
      return (
        <InputNumber
          value={
            dataIndex === 'total'
              ? Utils.formatCurrency(form.getFieldValue(dataIndex))
              : form.getFieldValue(dataIndex)
          }
          readOnly
          style={{
            border: 'none',
            textAlign: 'right',
            padding: 0,
          }}
          onBlur={handleBlur}
          formatter={(value) => {
            if (dataIndex === 'total') {
              return `£${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            } else if (dataIndex === 'vat') {
              return `${value}%`;
            }
            return value || '';
          }}
          parser={(value) => {
            if (dataIndex === 'total') {
              return parseFloat(value?.replace(/£\s?|(,*)/g, '') || '0');
            } else if (dataIndex === 'vat') {
              return parseFloat(value?.replace(/%/g, '') || '0');
            }
            return parseFloat(value || '0');
          }}
        />
      );
    }

    const commonStyle: React.CSSProperties = {
      width: '100%',
      display: !isExtra && ['costPrice', 'margin'].includes(dataIndex) ? 'none' : undefined,
    };

    if (isNumberType) {
      return (
        <InputNumber min={0} step="0.01" precision={2} style={commonStyle} onBlur={handleBlur} />
      );
    }

    return (
      <Input
        style={{
          ...commonStyle,
          textAlign: 'left',
        }}
        onBlur={handleBlur}
      />
    );
  }, [isNumberType, dataIndex, handleBlur, isExtra, readonlyFields]);

  return (
    <td
      className="custom-form-item"
      align={style?.textAlign as 'left' | 'center' | 'justify' | 'right' | 'char' | undefined}
      style={{
        ...style,
        whiteSpace: 'pre-line',
        wordWrap: 'break-word',
        maxWidth: '200px',
      }}
    >
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0, width, textAlign: 'right' }}
          rules={
            dataIndex === 'discount'
              ? [
                  {
                    validator: (_: any, value: any) => {
                      if (value === undefined || value === '') return Promise.resolve();
                      if (typeof value === 'number' && (value < 0 || value > 100)) {
                        return Promise.reject(
                          <p className="custom-validation-error">Value 0-100</p>
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]
              : [{ required, message: `${L('Required')}` }]
          }
        >
          {inputNode}
        </Form.Item>
      ) : (
        <div className="columnWrapper" style={{ whiteSpace: 'pre-line' }}>
          {children}
        </div>
      )}
    </td>
  );
};
